import { useEffect, useRef, useState } from 'react';
import { getTrackByRef } from '../services/api';
import { EnergyVisualizer, generateFallbackEnergyMap } from '../components/EnergyVisualizer';
import { buildTenantPath } from '../hooks/useTenant';

declare global {
  interface Window {
    FansAudioPlayer: any;
  }
}

interface TrackData {
  id: string;
  trackReference: string;
  title: string;
  coverArtUrl: string | null;
  streamUrl: string | null;
  hasAac: boolean;
  format: string;
  lyrics: any[] | null;
  enableLyrics: boolean;
  energyMap?: number[] | null;
}

interface PlayerPageProps {
  slug: string;
  trackRef: string;
}

// Cache-bust token generated once per page load — ensures fresh player lib on every deploy
const PLAYER_LIB_VERSION = String(Date.now());

/**
 * Eagerly load external resources (Font Awesome, player CSS, player JS)
 * in parallel with the API call so the player can render immediately
 * once track data arrives.
 */
function loadPlayerResources(): Promise<void> {
  // Font Awesome
  if (!document.querySelector('link[href*="font-awesome"]')) {
    const fa = document.createElement('link');
    fa.rel = 'stylesheet';
    fa.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css';
    document.head.appendChild(fa);
  }

  // Player CSS
  if (!document.querySelector('link[data-fap-css]')) {
    const css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = `/lib/fans-audio-player/fans-audio-player.css?t=${PLAYER_LIB_VERSION}`;
    css.setAttribute('data-fap-css', '1');
    document.head.appendChild(css);
  }

  // Player JS — return a promise that resolves when the script is loaded
  if (window.FansAudioPlayer) return Promise.resolve();

  return new Promise((resolve) => {
    const existing = document.querySelector('script[data-fap-js]');
    if (existing) {
      // Already loading — wait for it
      existing.addEventListener('load', () => resolve());
      if (window.FansAudioPlayer) resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = `/lib/fans-audio-player/fans-audio-player.js?t=${PLAYER_LIB_VERSION}`;
    script.setAttribute('data-fap-js', '1');
    script.onload = () => resolve();
    document.head.appendChild(script);
  });
}

export default function PlayerPage({ slug, trackRef }: PlayerPageProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const [track, setTrack] = useState<TrackData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resourcesReady, setResourcesReady] = useState(!!window.FansAudioPlayer);

  // Eagerly load player resources in parallel with API fetch
  useEffect(() => {
    loadPlayerResources().then(() => setResourcesReady(true));
  }, []);

  // Fetch track data from API
  useEffect(() => {
    async function fetchTrack() {
      try {
        const res = await getTrackByRef(trackRef);
        if (!res.ok) {
          setError('Track not found');
          return;
        }
        const json = await res.json() as { data: TrackData };
        setTrack(json.data);
      } catch {
        setError('Failed to load track');
      } finally {
        setLoading(false);
      }
    }

    fetchTrack();
  }, [slug, trackRef]);

  // Initialize player once BOTH track data AND resources are ready
  useEffect(() => {
    if (!track || !track.streamUrl || !resourcesReady) return;
    if (!containerRef.current || !window.FansAudioPlayer) return;

    const trackData = {
      src: track.streamUrl,
      title: track.title,
      format: track.format,
      lyrics: track.enableLyrics ? (track.lyrics || []) : [],
    };

    playerRef.current = new window.FansAudioPlayer(containerRef.current, trackData, {
      autoStart: false,
      overlayPlayPause: false,
      repeat: false,
      compact: true,
    });
    playerRef.current.mount();

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [track, resourcesReady]);

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-electric-cyan/30 border-t-electric-cyan rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !track) {
    return (
      <div className="min-h-screen bg-deep-charcoal flex flex-col items-center justify-center px-4">
        <h1 className="text-xl font-bold text-white mb-2">Track Not Found</h1>
        <p className="text-sm text-text-secondary font-mono mb-6">{error || 'This track does not exist.'}</p>
        <a
          href={buildTenantPath(slug)}
          className="text-xs text-electric-cyan/70 hover:text-electric-cyan font-mono transition-colors"
        >
          &lt; back to artist page
        </a>
      </div>
    );
  }

  const subtitleText = `now playing // ${track.format.toUpperCase()} stream`;

  return (
    <div className="min-h-screen bg-deep-charcoal flex flex-col items-center justify-center px-4 py-12">
      {/* Inline overrides to bypass CDN caching of external player CSS */}
      <style>{`
        .fap-compact .fap-osc-grid { display: none !important; }
        .fap-waveform-ghost, .fap-waveform { opacity: 0 !important; transition: opacity 0.3s ease; }
        .fap-waveform-ghost.fap-waveform-active { opacity: 0.25 !important; }
        .fap-waveform.fap-waveform-active { opacity: 1 !important; }
        .fap-compact .fap-lyric-display { height: 0 !important; padding-top: 0 !important; margin-top: 0 !important; overflow: hidden; transition: height 0.2s ease; }
        .fap-compact .fap-lyric-display.fap-lyric-active { height: 52px !important; padding-top: 8px !important; margin-top: 8px !important; }
        .fap-compact .fap-player-wrap { max-height: 260px; overflow: hidden; }
      `}</style>
      {/* Header with EnergyVisualizer background */}
      <div className="relative text-center mb-10 w-full max-w-4xl overflow-hidden rounded-lg px-6 py-10">
        {/* Energy waveform background */}
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <EnergyVisualizer
            energyMap={track.energyMap || generateFallbackEnergyMap(track.trackReference)}
            primaryColor="#00F5FF"
            backgroundColor="rgba(255,255,255,0.08)"
            barWidth={2}
            gap={1}
            progress={0}
            height="100%"
          />
        </div>
        {/* Content on top */}
        <div className="relative z-10">
          <a href={buildTenantPath(slug)} className="inline-flex items-center gap-2 mb-6 opacity-60 hover:opacity-100 transition-opacity">
            <span className="font-mono font-bold text-sm text-white">fans<span className="text-electric-cyan">.audio</span></span>
          </a>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-2">
            {track.title}
          </h1>
          <p className="text-sm text-text-secondary font-mono tracking-wide uppercase">
            {subtitleText}
          </p>
        </div>
      </div>

      {/* Player Container */}
      <div ref={containerRef} className="w-full max-w-4xl" />

      {/* Footer */}
      <div className="mt-10 text-center">
        <a
          href={buildTenantPath(slug)}
          className="text-xs text-electric-cyan/70 hover:text-electric-cyan font-mono transition-colors"
        >
          &lt; back to dashboard
        </a>
      </div>
    </div>
  );
}
